import { useState, type FormEvent } from 'react';

import type { TenantBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { slugify } from '@/shared/lib/slugify';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

export interface TenantFormValues {
  name: string;
  slug: string;
}

export interface TenantFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Заполнено — правим существующего, пусто — заводим нового.
   *
   * `undefined` здесь не «неизвестно», а осмысленное «нового», поэтому
   * записан явно: иначе строгий режим типов не даст передать его вовсе.
   */
  tenant?: TenantBody | undefined;
  onSubmit: (values: TenantFormValues) => Promise<void>;
}

/**
 * Форма тенанта: одна на создание и на изменение.
 *
 * Поля те же, разница только в заголовке и в том, что короткое имя при
 * создании подставляется из названия. Две почти одинаковые формы разошлись бы
 * при первой же правке — в одной поле бы добавили, во второй забыли.
 */
export function TenantFormDialog({ open, onOpenChange, tenant, onSubmit }: TenantFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Содержимое существует только пока окно открыто: набранное и ошибки
          сбрасываются сами, без эффекта на изменение open. */}
      {open && <Body tenant={tenant} onOpenChange={onOpenChange} onSubmit={onSubmit} />}
    </Dialog>
  );
}

function Body({ tenant, onOpenChange, onSubmit }: Omit<TenantFormDialogProps, 'open'>) {
  const editing = tenant !== undefined;

  const [name, setName] = useState(tenant?.name ?? '');
  const [slug, setSlug] = useState(tenant?.slug ?? '');
  // Пока короткое имя не трогали руками, оно едет следом за названием.
  // После первой правки — перестаёт: иначе человек печатает слаг, а тот
  // переписывается из-под пальцев при каждой букве в названии.
  const [slugTouched, setSlugTouched] = useState(editing);

  const [error, setError] = useState<string>();
  const [slugError, setSlugError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setSlugError(undefined);
    setBusy(true);

    onSubmit({ name: name.trim(), slug })
      .then(() => {
        onOpenChange(false);
      })
      .catch((cause: unknown) => {
        // Занятое короткое имя — ошибка конкретного поля, и показывать её
        // надо у него, а не общим сообщением внизу окна.
        if (cause instanceof ApiError && cause.status === 409) {
          setSlugError(cause.detail);
        } else if (cause instanceof ApiError) {
          setError(cause.detail);
        } else {
          setError('Не удалось сохранить. Попробуйте ещё раз.');
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <DialogContent
      title={editing ? 'Изменить тенанта' : 'Новый тенант'}
      description={editing ? undefined : 'Тенант — это клиент платформы. У него свои сайты и люди.'}
      size="sm"
      // Закрытие по клику мимо выключено: в форме уже что-то набрано, и
      // промах мышью не должен стирать работу. Крестик и «Отмена» на месте.
      dismissible={false}
      footer={
        <>
          <Button
            variant="outline"
            tone="neutral"
            type="button"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Отмена
          </Button>
          <Button type="submit" form="tenant-form" loading={busy}>
            {editing ? 'Сохранить' : 'Создать'}
          </Button>
        </>
      }
    >
      <form id="tenant-form" onSubmit={handleSubmit} noValidate>
        <Field label="Название" required error={error}>
          <Input
            value={name}
            autoFocus
            placeholder="Атомный диктант"
            onChange={(event) => {
              setName(event.target.value);

              if (!slugTouched) {
                setSlug(slugify(event.target.value));
              }
            }}
          />
        </Field>

        <Field
          label="Короткое имя"
          required
          error={slugError}
          hint="Строчные латинские буквы, цифры и дефис. Попадает в адреса."
        >
          <Input
            mono
            value={slug}
            placeholder="atomdiktant"
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value);
            }}
          />
        </Field>
      </form>
    </DialogContent>
  );
}
